import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  CheckboxCard,
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getNomncForm,
  updateNomncForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  BellIcon,
  MedicalNotesIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import {
  displayDateUS,
  formatDateForInput,
} from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  useDedicatedFormLoadEffect,
  useSessionUser,
} from "../useDedicatedFormLoadEffect";
import {
  ACKNOWLEDGEMENT_STATEMENT,
  APPEAL_DEADLINE_NOTE,
  APPEAL_INSTRUCTIONS,
  NON_COVERAGE_REASONS,
} from "./nomncContent";

const NomncForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useSessionUser();
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      setValues({
        serviceType: data.serviceType || "",
        effectiveEndDate: data.effectiveEndDate || "",

        reasonSkilledServicesNotRequired: Boolean(
          data.reasonSkilledServicesNotRequired,
        ),
        reasonNoLongerHomebound: Boolean(data.reasonNoLongerHomebound),
        reasonDocumentationNotSupported: Boolean(
          data.reasonDocumentationNotSupported,
        ),
        otherClinicalReason: data.otherClinicalReason || "",

        qioOrganizationName: data.qioOrganizationName || "",
        qioPhone: data.qioPhone || "",

        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useDedicatedFormLoadEffect(patientId, formId, isDraft, async (isCancelled) => {
    setLoading(true);

    if (isDraft) {
      hydrate({
        formName:
          clinicalForm.formName ||
          draft?.formName ||
          "Notice of Medicare Non-Coverage",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        ...draftCaregiverIdentity(currentUser, role),
        permissions: buildClinicalFormPermissions("pending", role),
      });
      if (!isCancelled()) {
        setLoading(false);
        onReady?.();
      }
      return;
    }

    try {
      const res = await getNomncForm(patientId, formId);
      if (isCancelled()) return;
      hydrate(res.result?.form || res.form);
    } catch (err) {
      if (isCancelled()) return;
      toast.error(err.message || "Failed to load form");
      onBack();
    } finally {
      if (isCancelled()) return;
      setLoading(false);
      onReady?.();
    }
  });

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  // Printing renders the identical sheet, only frozen: no editing, no
  // signing, no action buttons.
  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId: form?.filledBy || clinicalForm?.filledBy || (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const showPatientSignature = Boolean(permissions.canViewPatientSignature);
  /** Only the assigned clinician captures signatures; Admin/OA just read them. */
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const payload = { ...values, flag };
    payload.effectiveEndDate = values.effectiveEndDate
      ? formatDateForInput(values.effectiveEndDate)
      : null;

    // Admin/OA never sign, so signature keys are only sent by the clinician.
    return canSign
      ? { ...payload, ...signatures.buildSignaturePayload() }
      : payload;
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateNomncForm,
        filledByUserId,
        scheduleDate: values.effectiveEndDate
          ? formatDateForInput(values.effectiveEndDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;

  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const serviceLabel = values.serviceType || "Home Health Services";

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {/* Page chrome — stays outside the printed sheet */}
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      {/* The form itself — a single white sheet with margins on both sides */}
      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Notice of Medicare Non-Coverage (NOMNC)
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        {/* Shared identity strip: Patient / MRN / DOB / Caregiver / Date */}
        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || form.patientMrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.effectiveEndDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="effectiveEndDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.effectiveEndDate) || ""}
                  onChange={(event) =>
                    setValue("effectiveEndDate", event.target.value)
                  }
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
        />

        <FormMetaRow form={form} printMode={printMode} />

        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <Section icon={BellIcon} title="Notification of Service End">
          <p className="rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2 text-[11px] leading-snug text-slate-700">
            <strong className="font-bold">Important Notice:</strong> Your
            Medicare provider or health plan has determined that your{" "}
            <strong className="font-bold underline">{serviceLabel}</strong> will
            end on the date listed below. Medicare coverage for these services
            will no longer be provided after this date.
          </p>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Service Type">
              <input
                type="text"
                className={fieldClass}
                value={values.serviceType}
                disabled={!canEdit}
                maxLength={120}
                placeholder="Home Health Services"
                onChange={(e) => setValue("serviceType", e.target.value)}
              />
            </Field>
            <Field label="Effective Date of End of Coverage">
              {printMode ? (
                // A picker's placeholder and calendar icon read as content on
                // paper, so the date becomes plain text.
                <p className="h-7 text-[12px] font-bold text-slate-900">
                  {displayDateUS(values.effectiveEndDate) || "—"}
                </p>
              ) : (
                // Native date inputs render in the browser's locale, so the
                // shared picker is used to force MM/DD/YYYY.
                <InputDatePicker
                  name="effectiveEndDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.effectiveEndDate) || ""}
                  onChange={(event) =>
                    setValue("effectiveEndDate", event.target.value)
                  }
                  inputClassName={fieldClass}
                />
              )}
            </Field>
          </div>
          <p className="mt-1 text-[10px] italic text-slate-500">
            Service termination occurs at midnight on this date.
          </p>
        </Section>

        <Section icon={MedicalNotesIcon} title="Clinical Basis for Non-Coverage">
          <div className="space-y-1.5">
            {NON_COVERAGE_REASONS.map((item) => (
              <CheckboxCard
                key={item.key}
                item={item}
                checked={Boolean(values[item.key])}
                disabled={!canEdit}
                onChange={(checked) => setValue(item.key, checked)}
              />
            ))}
          </div>

          <div className="mt-3 border-t border-slate-100 pt-2.5">
            <Field label="Other Clinical Reason / Physician Communication">
              <textarea
                className={`${textareaClass} min-h-[64px]`}
                value={values.otherClinicalReason}
                disabled={!canEdit}
                maxLength={5000}
                placeholder="Enter detailed clinical justification or specifics from physician discussion..."
                onChange={(e) => setValue("otherClinicalReason", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={ShieldCheckIcon} title="Your Right to Appeal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 sm:divide-x sm:divide-slate-100">
            <div>
              <SubHeading>Immediate review process</SubHeading>
              <p className="text-[11px] leading-snug text-slate-600">
                {APPEAL_INSTRUCTIONS}
              </p>
              <p className="mt-2 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] font-semibold text-sky-900">
                <AlertCircleIcon size={12} className="mt-0.5 shrink-0" />
                {APPEAL_DEADLINE_NOTE}
              </p>
            </div>

            <div className="sm:pl-5">
              <SubHeading>Contact information</SubHeading>
              <div className="space-y-2">
                <Field label="Organization">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.qioOrganizationName}
                    disabled={!canEdit}
                    maxLength={255}
                    placeholder="Quality Improvement Organization"
                    onChange={(e) =>
                      setValue("qioOrganizationName", e.target.value)
                    }
                  />
                </Field>
                <Field label="Phone Number">
                  <div className="flex items-center gap-1.5">
                    <PhoneIcon size={12} className="shrink-0 text-slate-400" />
                    <input
                      type="tel"
                      className={fieldClass}
                      value={values.qioPhone}
                      disabled={!canEdit}
                      maxLength={60}
                      placeholder="1-800-MEDICARE (1-877-588-1123)"
                      onChange={(e) => setValue("qioPhone", e.target.value)}
                    />
                  </div>
                </Field>
              </div>
            </div>
          </div>
        </Section>

        {/* Receipt is not agreement, so the notice says so above the signature. */}
        <p className="px-1 text-[11px] italic leading-snug text-slate-600">
          {ACKNOWLEDGEMENT_STATEMENT}
        </p>

        {/* Signature sections close out every form, above the office review. */}
        <FormSignatureSections
          {...signatureProps}
          patientSignatureUrl={
            showPatientSignature ? signatureProps.patientSignatureUrl : null
          }
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {/* On paper the comments are always static text, never a textarea. */}
        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

NomncForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  /** Renders the sheet frozen and without chrome, for the print page. */
  printMode: PropTypes.bool,
  /** Fired once the form's own data has loaded. */
  onReady: PropTypes.func,
};

export default NomncForm;
